import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthApi } from '../api-service/auth/auth.api';
import { ACHIEVEMENT_POPUP_SEEN_KEY } from '../constants/app.constants';
import { Messages } from '../constants/messages';
import { CommonService } from '../helper/common.service';
import { DEVICE_ID_KEY } from '../helper/device.helper';
import { AuthUser } from '../models/auth.model';
import { IdleService } from './idle.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

    readonly dialog = inject(MatDialog);
    private userSubject = new BehaviorSubject<AuthUser | null>(null);

    user$ = this.userSubject.asObservable();

    private loggedInSubject = new BehaviorSubject<boolean>(false);
    isLoggedIn$ = this.loggedInSubject.asObservable();

    constructor(
        private authApi: AuthApi,
        private commonService: CommonService,
        private zone: NgZone,
        private idleService: IdleService,
        private router: Router,
    ) {
        const token = localStorage.getItem('accessToken');
        this.restoreSession(token);
     }

     // ==================================================
    // RESTORE SESSION AFTER REFRESH
    // ==================================================
    restoreSession(token: string | null): void {

        if (!token) return;

        // instant login state
        this.loggedInSubject.next(true);

        // instant user restore from localStorage
        const cachedUser = this.getUserCache();

        if (cachedUser) {
            this.userSubject.next(cachedUser);
        }

        // silent backend verify
        this.loadUserFromServer((res: any) => {
            if (res?.success && res.data) {
                this.setUser(res.data);
                this.onLoginSuccess();
            } else {
                this.logout(() => {
                    this.router.navigate(['/auth/login']);
                });
            }
        });
    }

    /**
   * Call AFTER successful login
   */
    onLoginSuccess(): void {
        this.idleService.start(() => {
            // 🔥 AUTO LOGOUT ON IDLE
            this.logout(() => {
                this.router.navigate(['/auth/login']);
            }, true);
        });
    }

    hasRole(...roles: string[]): boolean {
        const role = this.getUser()?.role;
        return !!role && roles.includes(role);
    }

    /** Set user after login/OTP */
    setUser(user: AuthUser | null) {
        this.userSubject.next(user);
        if (user) {
            this.saveUserCache(user);
            this.loggedInSubject.next(true);
        }
    }

    private getUserCache(): AuthUser | null {
        const raw = localStorage.getItem('user');

        if (!raw) return null;

        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    private saveUserCache(user: any): void {
        const minimalUser = {
            _id: user?._id,
            role: user?.role,
            firstName: user?.firstName,
            lastName: user?.lastName,
            studySiteLocation: user?.studySiteLocation,
        };

        localStorage.setItem('user', JSON.stringify(minimalUser));
    }

    updateAvatar(avatarUrl: string) {
        const user = this.userSubject.value;
        if (!user) return;

        const updatedUser = {
            ...user,
            avatarUrl
        };

        this.userSubject.next(updatedUser);
        this.saveUserCache(updatedUser);
    }

    getUser() {
        return this.userSubject.value;
    }

    get snapshot() {
        return this.userSubject.value;
    }

    getRole() {
        return this.userSubject.value?.role ?? null;
    }

    clearUser() {
        this.userSubject.next(null);
        this.loggedInSubject.next(false);
    }


    logout(onSuccess: () => void, isIdle: boolean = false) {
        this.commonService.showLoader();

        this.authApi.logOut().subscribe({
            next: (res: any) => {
                this.clearStorageKeepRemember();
                this.clearUser();

                // Optional UX message for idle logout
                if (isIdle) {
                    this.dialog.closeAll();
                    this.commonService.warning(Messages.AUTH.ACCOUNT_IDLE_LOGOUT);
                }
                // Callback (navigation handled by caller)
                onSuccess?.();
            },
            error: (err) => {
                this.commonService.error(err.message || Messages.AUTH.FETCH_USER_FAILED);
                this.commonService.hideLoader();
            },
            complete: () => this.commonService.hideLoader()
        });
    }

    private clearStorageKeepRemember(): void {
        const rememberMe = localStorage.getItem('remember_me');
        const rememberEmail = localStorage.getItem('remember_email');
        const rememberPassword = localStorage.getItem('remember_password');
        // Survives logout by design: it identifies the browser, not the user.
        // Clearing it would hand the next login a brand-new device id, and
        // Manage Sessions would collect a fresh row on every sign-in.
        const deviceId = localStorage.getItem(DEVICE_ID_KEY);
        // Also survives logout by design: it records which achievement popups
        // each user id has already been shown, and clearing it would replay
        // every "Congratulations!" on the next sign-in.
        const achievementPopupSeen = localStorage.getItem(ACHIEVEMENT_POPUP_SEEN_KEY);

        localStorage.clear();

        if (deviceId !== null) {
            localStorage.setItem(DEVICE_ID_KEY, deviceId);
        }

        if (achievementPopupSeen !== null) {
            localStorage.setItem(ACHIEVEMENT_POPUP_SEEN_KEY, achievementPopupSeen);
        }

        if (rememberMe !== null) {
            localStorage.setItem('remember_me', rememberMe);
        }

        if (rememberEmail !== null) {
            localStorage.setItem('remember_email', rememberEmail);
        }

        if (rememberPassword !== null) {
            localStorage.setItem('remember_password', rememberPassword);
        }
    }

    saveToken(data: any) {
        localStorage.setItem('accessToken', data.token.accessToken);
        //localStorage.setItem('role', data.user.role);

        this.loggedInSubject.next(true);
    }

    getToken() {
        return localStorage.getItem('accessToken');
    }

    isLoggedIn(): boolean {
        return !!this.getToken();
    }

    /** Rehydrate user after page refresh */
    loadUserFromServer(
        onSuccess: (res: any) => void
    ) {

        this.authApi.userMe()
            .subscribe({
                next: (res) => onSuccess(res),
                error: (err) => {
                    this.clearStorageKeepRemember();
                    this.clearUser();
                    this.commonService.hideLoader()
                    this.commonService.error(err.message || Messages.AUTH.FETCH_USER_FAILED);
                },
                complete: () => this.commonService.hideLoader()
            });

    }
}
